import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { 
  IconWallet, IconPlus, IconListDetails, IconUser, IconCalendar, IconArrowUpRight, IconArrowDownLeft, IconArrowsExchange
} from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";

export default function BanksPage() {
  const [tab, setTab] = useState("accounts"); // accounts, owners, fund_transfers, journals
  const [loading, setLoading] = useState(false);

  // Data lists
  const [accounts, setAccounts] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownerTransactions, setOwnerTransactions] = useState([]);
  const [journals, setJournals] = useState([]);
  const [fundTransfers, setFundTransfers] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  // Modal / Form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // account, owner, owner_transaction, fund_transfer, journal

  const [newAccountForm, setNewAccountForm] = useState({ name: "", type: "Asset", sub_type: "" });
  const [newOwnerForm, setNewOwnerForm] = useState({ name: "", email: "", phone: "", share_percentage: 0 });
  const [newOwnerTxForm, setNewOwnerTxForm] = useState({ owner_id: "", transaction_type: "Add Fund", amount: 0, payment_type_id: "", reference: "", notes: "" });
  const [newFundTransferForm, setNewFundTransferForm] = useState({ from_payment_type_id: "", to_payment_type_id: "", amount: 0, reference: "", notes: "" });
  const [newJournalForm, setNewJournalForm] = useState({
    entryDate: new Date().toISOString().slice(0, 10),
    account: "",
    debit: 0,
    credit: 0,
    description: ""
  });

  useEffect(() => {
    fetchTabData();
  }, [tab]);

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (tab === "accounts") {
        const res = await apiClient.get("/accounting/accounts");
        setAccounts(res.data);
      } else if (tab === "owners") {
        const [ownRes, ptRes] = await Promise.all([
          apiClient.get("/finance/owners"),
          apiClient.get("/settings/payment-types")
        ]);
        setOwners(ownRes.data);
        setPaymentTypes(ptRes.data);
      } else if (tab === "fund_transfers") {
        const [ftRes, ptRes] = await Promise.all([
          apiClient.get("/finance/fund-transfers"),
          apiClient.get("/settings/payment-types")
        ]);
        setFundTransfers(ftRes.data);
        setPaymentTypes(ptRes.data);
      } else if (tab === "journals") {
        const [jrRes, accRes] = await Promise.all([
          apiClient.get("/accounting/journal"),
          apiClient.get("/accounting/accounts")
        ]);
        setJournals(jrRes.data);
        setAccounts(accRes.data);
      }
    } catch (e) {
      toast.error("Failed to load accounting data");
    } finally {
      setLoading(false);
    }
  };

  const loadOwnerTransactions = async (ownerId) => {
    try {
      const res = await apiClient.get(`/finance/owners/${ownerId}/transactions`);
      setOwnerTransactions(res.data);
    } catch (e) {
      toast.error("Failed to load owner transactions");
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/accounting/accounts", newAccountForm);
      toast.success("Account added to Chart of Accounts");
      setIsModalOpen(false);
      setNewAccountForm({ name: "", type: "Asset", sub_type: "" });
      fetchTabData();
    } catch (err) {
      toast.error("Failed to add account");
    }
  };

  const handleCreateOwner = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/finance/owners", newOwnerForm);
      toast.success("Owner registered successfully");
      setIsModalOpen(false);
      setNewOwnerForm({ name: "", email: "", phone: "", share_percentage: 0 });
      fetchTabData();
    } catch (err) {
      toast.error("Failed to register owner");
    }
  };

  const handleCreateOwnerTx = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/finance/owners/${newOwnerTxForm.owner_id}/transactions`, newOwnerTxForm);
      toast.success(`Owner ${newOwnerTxForm.transaction_type} successful`);
      setIsModalOpen(false);
      setNewOwnerTxForm({ owner_id: "", transaction_type: "Add Fund", amount: 0, payment_type_id: "", reference: "", notes: "" });
      fetchTabData();
    } catch (err) {
      toast.error("Failed to process owner transaction");
    }
  };

  const handleCreateFundTransfer = async (e) => {
    e.preventDefault();
    if(newFundTransferForm.from_payment_type_id === newFundTransferForm.to_payment_type_id) {
      return toast.error("Cannot transfer to the same payment type");
    }
    try {
      await apiClient.post("/finance/fund-transfers", newFundTransferForm);
      toast.success("Fund transferred successfully");
      setIsModalOpen(false);
      setNewFundTransferForm({ from_payment_type_id: "", to_payment_type_id: "", amount: 0, reference: "", notes: "" });
      fetchTabData();
    } catch (err) {
      toast.error("Failed to transfer funds");
    }
  };

  const handleCreateJournal = async (e) => {
    e.preventDefault();
    if (!newJournalForm.account) {
      toast.error("Please select an account");
      return;
    }
    if (Number(String(newJournalForm.debit).replace(/,/g, "")) === 0 && Number(String(newJournalForm.credit).replace(/,/g, "")) === 0) {
      toast.error("Debit or Credit amount must be greater than zero");
      return;
    }
    try {
      await apiClient.post("/accounting/journal", {
        entryDate: newJournalForm.entryDate,
        account: newJournalForm.account,
        debit: Number(String(newJournalForm.debit).replace(/,/g, "")),
        credit: Number(String(newJournalForm.credit).replace(/,/g, "")),
        description: newJournalForm.description
      });
      toast.success("Journal entry posted successfully");
      setIsModalOpen(false);
      setNewJournalForm({
        entryDate: new Date().toISOString().slice(0, 10),
        account: "",
        debit: 0,
        credit: 0,
        description: ""
      });
      fetchTabData();
    } catch (err) {
      toast.error("Failed to post journal entry");
    }
  };

  const getPaymentTypeName = (id) => {
    const pt = paymentTypes.find(p => p.id == id);
    return pt ? pt.title : 'Unknown';
  };

  return (
    <div className="p-6 min-h-screen bg-restro-green-light dark:bg-restro-gray text-restro-text dark:text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Bank & Cash Ledger Hub</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage Cash/Bank Transfers, Owner Equity, and Chart of Accounts.</p>
        </div>
        <div className="flex gap-2">
          {tab === "owners" && (
            <button
              onClick={() => { setModalType("owner_transaction"); setIsModalOpen(true); }}
              className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center gap-2 shadow-sm transition"
            >
              <IconArrowsExchange size={18} /> Owner Equity Tx
            </button>
          )}
          <button
            onClick={() => { setModalType(tab); setIsModalOpen(true); }}
            className="px-5 py-3 rounded-2xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold flex items-center gap-2 shadow-sm transition"
          >
            <IconPlus size={18} /> Add {tab === "accounts" ? "Account" : tab === "owners" ? "Owner Profile" : tab === "fund_transfers" ? "Fund Transfer" : "Journal Entry"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-zinc-800 gap-6 mb-8 overflow-x-auto">
        {[
          { id: "accounts", label: "Chart of Accounts", icon: <IconWallet size={18} /> },
          { id: "owners", label: "Owners & Equity", icon: <IconUser size={18} /> },
          { id: "fund_transfers", label: "Fund Transfers", icon: <IconArrowsExchange size={18} /> },
          { id: "journals", label: "Journal Entries", icon: <IconListDetails size={18} /> }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-4 px-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 transition whitespace-nowrap ${
              tab === t.id 
                ? "border-restro-green text-restro-green" 
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-restro-green"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
          {/* TAB 1: ACCOUNTS TABLE */}
          {tab === "accounts" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Account Name</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Account Type</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Sub-Type / Class</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {Array.isArray(accounts) && accounts.map(acc => (
                    <tr key={acc.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{acc.name}</td>
                      <td className="p-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          acc.type === "Asset" ? "bg-green-50 text-green-600 dark:bg-green-950/40 dark:text-green-400" :
                          acc.type === "Liability" ? "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400" :
                          acc.type === "Revenue" ? "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400" :
                          acc.type === "Expense" ? "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400" :
                          "bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400"
                        }`}>
                          {acc.type}
                        </span>
                      </td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{acc.sub_type || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 2: OWNERS TABLE */}
          {tab === "owners" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Owner Name</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Contact</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Share %</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Status</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {Array.isArray(owners) && owners.map(sh => (
                    <tr key={sh.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{sh.name}</td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{sh.email} <br/> {sh.phone}</td>
                      <td className="p-4 text-blue-600 font-bold">{sh.share_percentage}%</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${sh.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {sh.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <button onClick={() => {
                          loadOwnerTransactions(sh.id);
                          document.getElementById('modal_owner_transactions').showModal();
                        }} className="text-restro-green font-bold text-xs hover:underline">
                          View Transactions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 3: FUND TRANSFERS */}
          {tab === "fund_transfers" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Transfer Date</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">From (Payment Type)</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">To (Payment Type)</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Amount</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Reference / Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {Array.isArray(fundTransfers) && fundTransfers.map(ft => (
                    <tr key={ft.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-medium">{new Date(ft.transfer_date).toLocaleDateString()}</td>
                      <td className="p-4 text-red-500 font-bold">{getPaymentTypeName(ft.from_payment_type_id)}</td>
                      <td className="p-4 text-green-500 font-bold">{getPaymentTypeName(ft.to_payment_type_id)}</td>
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{ft.amount}</td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{ft.reference}<br/><span className="text-xs">{ft.notes}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 4: JOURNALS TABLE */}
          {tab === "journals" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Date</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Ledger Account</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Description</th>
                    <th className="p-4 text-right font-bold text-gray-500 uppercase tracking-wider text-[11px]">Debit (Rs.)</th>
                    <th className="p-4 text-right font-bold text-gray-500 uppercase tracking-wider text-[11px]">Credit (Rs.)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {Array.isArray(journals) && journals.map(jr => (
                    <tr key={jr.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{new Date(jr.entry_date).toLocaleDateString()}</td>
                      <td className="p-4 font-bold text-gray-900 dark:text-white">{jr.account}</td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{jr.description}</td>
                      <td className="p-4 text-right text-green-600 font-semibold">{Number(String(jr.debit).replace(/,/g, "")) > 0 ? Number(String(jr.debit).replace(/,/g, "")).toFixed(2) : "-"}</td>
                      <td className="p-4 text-right text-red-600 font-semibold">{Number(String(jr.credit).replace(/,/g, "")) > 0 ? Number(String(jr.credit).replace(/,/g, "")).toFixed(2) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Owner Transactions Modal */}
      <dialog id="modal_owner_transactions" className="modal">
        <div className="modal-box w-11/12 max-w-4xl rounded-3xl dark:bg-zinc-900 border dark:border-zinc-800">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          <h3 className="font-bold text-xl mb-6">Owner Transaction History</h3>
          <div className="overflow-x-auto">
            <table className="table w-full text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800">
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Payment Type</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(ownerTransactions) && ownerTransactions.map(tx => (
                  <tr key={tx.id}>
                    <td>{new Date(tx.transaction_date).toLocaleString()}</td>
                    <td>
                      <span className={`badge badge-sm font-bold ${tx.transaction_type === 'Add Fund' ? 'badge-success' : tx.transaction_type === 'Share Profit' ? 'badge-info bg-blue-100 text-blue-700' : 'badge-error'}`}>
                        {tx.transaction_type}
                      </span>
                    </td>
                    <td className="font-bold">{tx.amount}</td>
                    <td>{getPaymentTypeName(tx.payment_type_id)}</td>
                    <td>{tx.reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {ownerTransactions.length === 0 && <p className="text-center p-6 text-gray-500">No transactions found.</p>}
          </div>
        </div>
      </dialog>

      {/* Add Modals */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full max-w-md shadow-2xl p-6 space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                {modalType === "accounts" ? "Add Account" : 
                 modalType === "owners" ? "Add Owner Profile" : 
                 modalType === "owner_transaction" ? "Owner Equity Transaction" :
                 modalType === "fund_transfers" ? "Transfer Funds" : "Journal Entry"}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>

            {/* Form: Account */}
            {modalType === "accounts" && (
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Account Name</label>
                  <input
                    type="text"
                    required
                    value={newAccountForm.name}
                    onChange={e => setNewAccountForm({ ...newAccountForm, name: e.target.value })}
                    className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                    placeholder="e.g. Nabil Bank A/C"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Type</label>
                    <select
                      value={newAccountForm.type}
                      onChange={e => setNewAccountForm({ ...newAccountForm, type: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                    >
                      <option value="Asset">Asset</option>
                      <option value="Liability">Liability</option>
                      <option value="Equity">Equity</option>
                      <option value="Revenue">Revenue</option>
                      <option value="Expense">Expense</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Class / Sub-Type</label>
                    <input
                      type="text"
                      value={newAccountForm.sub_type}
                      onChange={e => setNewAccountForm({ ...newAccountForm, sub_type: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                    />
                  </div>
                </div>
                <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Ledger Account</button>
              </form>
            )}

            {/* Form: Owner Profile */}
            {modalType === "owners" && (
              <form onSubmit={handleCreateOwner} className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Owner Name</label>
                  <input
                    type="text"
                    required
                    value={newOwnerForm.name}
                    onChange={e => setNewOwnerForm({ ...newOwnerForm, name: e.target.value })}
                    className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Email & Phone</label>
                  <input type="email" placeholder="Email" value={newOwnerForm.email} onChange={e => setNewOwnerForm({ ...newOwnerForm, email: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none mb-2" />
                  <input type="text" placeholder="Phone" value={newOwnerForm.phone} onChange={e => setNewOwnerForm({ ...newOwnerForm, phone: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Share Percentage (%)</label>
                  <input type="number" step="0.01" value={newOwnerForm.share_percentage} onChange={e => setNewOwnerForm({ ...newOwnerForm, share_percentage: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </div>
                <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Register Owner</button>
              </form>
            )}

            {/* Form: Owner Transaction */}
            {modalType === "owner_transaction" && (
              <form onSubmit={handleCreateOwnerTx} className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Select Owner</label>
                  <select required value={newOwnerTxForm.owner_id} onChange={e => setNewOwnerTxForm({ ...newOwnerTxForm, owner_id: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none">
                    <option value="">Select Owner</option>
                    {Array.isArray(owners) && owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Action</label>
                    <select value={newOwnerTxForm.transaction_type} onChange={e => setNewOwnerTxForm({ ...newOwnerTxForm, transaction_type: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none">
                      <option value="Add Fund">Add Fund</option>
                      <option value="Withdraw Fund">Withdraw Fund</option>
                      <option value="Share Profit">Share Profit</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Amount</label>
                    <input type="number" required min="1" value={newOwnerTxForm.amount} onChange={e => setNewOwnerTxForm({ ...newOwnerTxForm, amount: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Payment Source/Destination</label>
                  <select required value={newOwnerTxForm.payment_type_id} onChange={e => setNewOwnerTxForm({ ...newOwnerTxForm, payment_type_id: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none">
                    <option value="">Select Payment Type</option>
                    {Array.isArray(paymentTypes) && paymentTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.title}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Reference</label>
                  <input type="text" value={newOwnerTxForm.reference} onChange={e => setNewOwnerTxForm({ ...newOwnerTxForm, reference: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </div>
                <button type="submit" className="w-full py-4 rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white transition">Process Transaction</button>
              </form>
            )}

            {/* Form: Fund Transfer */}
            {modalType === "fund_transfers" && (
              <form onSubmit={handleCreateFundTransfer} className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Transfer From</label>
                  <select required value={newFundTransferForm.from_payment_type_id} onChange={e => setNewFundTransferForm({ ...newFundTransferForm, from_payment_type_id: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none">
                    <option value="">Select Source Account</option>
                    {Array.isArray(paymentTypes) && paymentTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.title}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Transfer To</label>
                  <select required value={newFundTransferForm.to_payment_type_id} onChange={e => setNewFundTransferForm({ ...newFundTransferForm, to_payment_type_id: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none">
                    <option value="">Select Destination Account</option>
                    {Array.isArray(paymentTypes) && paymentTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.title}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Amount</label>
                  <input type="number" required min="1" value={newFundTransferForm.amount} onChange={e => setNewFundTransferForm({ ...newFundTransferForm, amount: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Reference / Notes</label>
                  <input type="text" value={newFundTransferForm.reference} onChange={e => setNewFundTransferForm({ ...newFundTransferForm, reference: e.target.value })} className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </div>
                <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Execute Transfer</button>
              </form>
            )}

            {/* Form: Journal Entry */}
            {modalType === "journals" && (
              <form onSubmit={handleCreateJournal} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Posting Date</label>
                    <input
                      type="date"
                      required
                      value={newJournalForm.entryDate}
                      onChange={e => setNewJournalForm({ ...newJournalForm, entryDate: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Ledger Account</label>
                    <select
                      required
                      value={newJournalForm.account}
                      onChange={e => setNewJournalForm({ ...newJournalForm, account: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                    >
                      <option value="">Select Account</option>
                      {Array.isArray(accounts) && accounts.map(acc => (
                        <option key={acc.id} value={acc.name}>{acc.name} ({acc.type})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Debit (Dr.)</label>
                    <input
                      type="number"
                      value={newJournalForm.debit}
                      onChange={e => setNewJournalForm({ ...newJournalForm, debit: e.target.value, credit: 0 })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                      min="0"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Credit (Cr.)</label>
                    <input
                      type="number"
                      value={newJournalForm.credit}
                      onChange={e => setNewJournalForm({ ...newJournalForm, credit: e.target.value, debit: 0 })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Description / Memo</label>
                  <input
                    type="text"
                    required
                    value={newJournalForm.description}
                    onChange={e => setNewJournalForm({ ...newJournalForm, description: e.target.value })}
                    className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                    placeholder="Entry explanation"
                  />
                </div>

                <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Post Journal Entry</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
